/**
 * PulseFit / RoxKit Central Reactive Proxy State Store
 * 
 * Provides an encapsulated, deep-reactive Proxy store with:
 * - Recursive mutation trapping for objects and arrays
 * - Microtask-coalesced notification batching (queueMicrotask)
 * - Key-targeted and global subscription APIs
 * - Selective auto-persistence to localStorage ('pulsefit_state_v2')
 * - Ergonomic domain action setters
 * - Zero-churn backwards compatibility for legacy state consumers
 */

export const STORAGE_KEY = 'pulsefit_state_v2';
export const RAW_STATE_KEY = Symbol('RAW_STATE');

/**
 * Returns Monday of the week containing dateInput.
 * @param {Date|string|number} [dateInput=new Date()]
 * @returns {Date} Monday at 00:00:00.000 local time
 */
export function getMondayOfCurrentWeek(dateInput = new Date()) {
  let d;
  if (typeof dateInput === 'string') {
    const parts = dateInput.split('T')[0].split('-');
    if (parts.length === 3) {
      d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10), 12, 0, 0);
    } else {
      d = new Date(dateInput);
    }
  } else if (typeof dateInput === 'number') {
    d = new Date(dateInput);
  } else if (dateInput instanceof Date) {
    d = new Date(dateInput.getTime());
  } else {
    d = new Date();
  }

  if (isNaN(d.getTime())) d = new Date();

  const day = d.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const diffToMonday = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diffToMonday, 0, 0, 0, 0);
}

/**
 * Normalized factory returning initial default state.
 */
export function getInitialState() {
  return {
    today: '',
    currentUser: null,
    isGuest: false,
    currentWeekStart: getMondayOfCurrentWeek(),
    workouts: [],
    history: [],
    todayHealth: null,
    dailyRecovery: null,
    goals: {
      suffer: 300,
      distance: 15.0,
      calories: 2000,
      stations: {
        SkiErg: 2000,
        'Sled Push': 50,
        'Sled Pull': 50,
        'Burpee Broad Jumps': 100,
        Rowing: 2000,
        'Farmers Carry': 120,
        'Sandbag Lunges': 100,
        'Wall Balls': 100
      }
    },
    weeklyZones: { z2: 0, z3: 0, z4: 0, z5: 0 },
    weeklyZoneAdjustments: { z2: 0, z3: 0, z4: 0, z5: 0 },
    weeklyZoneGoals: { z2: 120, z3: 60, z4: 60, z5: 30 },
    weeklyAdjustments: { suffer: 0, distance: 0, calories: 0 },
    hrThresholds: { z2Min: 125, z3Min: 141, z4Min: 156, z5Min: 170 },
    stationAlternatives: null,
    uiPreferences: { showLogButton: false },
    strava: {
      syncMode: 'push_only',
      clientId: '',
      clientSecret: '',
      redirectUri: 'http://localhost:8080',
      accessToken: '',
      refreshToken: '',
      expiresAt: 0,
      athleteName: ''
    },
    geminiApiKey: '',
    geminiInstructions: '',
    lastRecommendation: null,
    comparisons: {},
    plannedWorkouts: [],
    biometricLockEnabled: false,
    isBiometricLocked: false
  };
}

// Persistable keys whitelist (sensitive sessions, tokens, and modals are strictly excluded)
const PERSISTABLE_KEYS = new Set([
  'goals',
  'hrThresholds',
  'hrZoneThresholds',
  'stationAlternatives',
  'weeklyAdjustments',
  'weeklyZoneAdjustments',
  'weeklyZoneGoals',
  'uiPreferences',
  'strava',
  'geminiApiKey',
  'geminiInstructions',
  'comparisons',
  'plannedWorkouts',
  'biometricLockEnabled',
  'dailyRecovery',
  'todayHealth'
]);

// Caches for proxy referential identity and inverse unwrapping
const proxyCache = new WeakMap();
const reverseProxyCache = new WeakMap();
const rootKeyMap = new WeakMap();

// Microtask batching and subscription state
let isBatchScheduled = false;
const pendingMutatedKeys = new Set();
const subscribersByKey = new Map();
const globalSubscribers = new Set();

/**
 * Checks whether an object can and should be wrapped in a reactive Proxy.
 */
function isProxyable(val) {
  if (!val || typeof val !== 'object') return false;
  if (val instanceof Date) return false;
  if (val instanceof RegExp) return false;
  if (val instanceof Map || val instanceof Set) return false;
  if (val instanceof Promise) return false;
  if (typeof Element !== 'undefined' && val instanceof Element) return false;
  return Array.isArray(val) || Object.prototype.toString.call(val) === '[object Object]';
}

/**
 * Schedules a microtask flush for state mutations.
 */
function scheduleBatch(key) {
  if (key && typeof key === 'string') {
    pendingMutatedKeys.add(key);
    if (key === 'hrThresholds') pendingMutatedKeys.add('hrZoneThresholds');
    if (key === 'hrZoneThresholds') pendingMutatedKeys.add('hrThresholds');
  }

  if (!isBatchScheduled) {
    isBatchScheduled = true;
    queueMicrotask(flushBatch);
  }
}

/**
 * Flushes pending mutations immediately, notifying subscribers and triggering persistence.
 */
export function flushStoreUpdates() {
  if (isBatchScheduled || pendingMutatedKeys.size > 0) {
    flushBatch();
  }
}

function flushBatch() {
  isBatchScheduled = false;
  const mutatedKeys = Array.from(pendingMutatedKeys);
  pendingMutatedKeys.clear();

  if (mutatedKeys.length === 0) return;

  // 1. Auto-persistence check
  const shouldPersist = mutatedKeys.some(k => PERSISTABLE_KEYS.has(k));
  if (shouldPersist) {
    saveStateToStorage();
  }

  // 2. Mirror window.cachedWorkouts for bridge compatibility
  if (mutatedKeys.includes('workouts') && typeof window !== 'undefined') {
    window.cachedWorkouts = store.workouts;
  }

  // 3. Notify key-targeted subscribers
  for (const key of mutatedKeys) {
    const subs = subscribersByKey.get(key);
    if (subs && subs.size > 0) {
      const val = store[key];
      Array.from(subs).forEach(callback => {
        try {
          callback(val, key);
        } catch (err) {
          console.error(`[Store] Subscriber error for key "${key}":`, err);
        }
      });
    }
  }

  // 4. Notify global subscribers
  if (globalSubscribers.size > 0) {
    Array.from(globalSubscribers).forEach(callback => {
      try {
        callback(mutatedKeys, store);
      } catch (err) {
        console.error('[Store] Global subscriber error:', err);
      }
    });
  }
}

/**
 * Creates a recursive deep proxy for the given target and root key.
 */
function createDeepProxy(target, rootKey = null) {
  if (!isProxyable(target)) return target;

  if (rootKey) {
    rootKeyMap.set(target, rootKey);
  }

  const existingProxy = proxyCache.get(target);
  if (existingProxy) return existingProxy;

  const handler = {
    get(t, prop, receiver) {
      if (prop === RAW_STATE_KEY) return t;

      // Interoperability alias: hrZoneThresholds <-> hrThresholds
      if (t === rawState) {
        if (prop === 'hrZoneThresholds' || prop === 'hrThresholds') {
          return createDeepProxy(t.hrThresholds || t.hrZoneThresholds, 'hrThresholds');
        }
      }

      // Transparent array helper methods handling raw vs proxy equality and negative indices
      if (Array.isArray(t)) {
        if (prop === 'includes') {
          return function(item, fromIndex = 0) {
            const rawItem = reverseProxyCache.get(item) || item;
            const start = fromIndex < 0 ? Math.max(0, t.length + fromIndex) : fromIndex;
            for (let i = start; i < t.length; i++) {
              const el = t[i];
              const rawEl = reverseProxyCache.get(el) || el;
              if (el === item || rawEl === rawItem || Object.is(rawEl, rawItem)) return true;
            }
            return false;
          };
        }
        if (prop === 'indexOf') {
          return function(item, fromIndex = 0) {
            const rawItem = reverseProxyCache.get(item) || item;
            const start = fromIndex < 0 ? Math.max(0, t.length + fromIndex) : fromIndex;
            for (let i = start; i < t.length; i++) {
              const el = t[i];
              const rawEl = reverseProxyCache.get(el) || el;
              if (el === item || rawEl === rawItem) return i;
            }
            return -1;
          };
        }
        if (prop === 'lastIndexOf') {
          return function(item, fromIndex = t.length - 1) {
            const rawItem = reverseProxyCache.get(item) || item;
            let start = fromIndex;
            if (start < 0) {
              start = t.length + start;
            } else if (start >= t.length) {
              start = t.length - 1;
            }
            for (let i = start; i >= 0; i--) {
              const el = t[i];
              const rawEl = reverseProxyCache.get(el) || el;
              if (el === item || rawEl === rawItem) return i;
            }
            return -1;
          };
        }
      }

      const val = Reflect.get(t, prop, receiver);

      if (typeof prop === 'symbol') return val;

      const currentRoot = rootKeyMap.get(t) || (t === rawState ? (prop === 'hrZoneThresholds' ? 'hrThresholds' : prop) : null);
      if (isProxyable(val)) {
        return createDeepProxy(val, currentRoot);
      }
      return val;
    },

    set(t, prop, value) {
      const rawValue = reverseProxyCache.get(value) || value;
      let effectiveProp = prop;

      if (t === rawState && prop === 'hrZoneThresholds') {
        effectiveProp = 'hrThresholds';
      }

      const currentRoot = rootKeyMap.get(t) || (t === rawState ? (effectiveProp === 'hrZoneThresholds' ? 'hrThresholds' : effectiveProp) : null);

      const success = Reflect.set(t, effectiveProp, rawValue);
      if (success) {
        scheduleBatch(currentRoot || effectiveProp);
      }
      return success;
    },

    has(t, prop) {
      if (t === rawState && prop === 'hrZoneThresholds') {
        return 'hrThresholds' in t || 'hrZoneThresholds' in t;
      }
      return Reflect.has(t, prop);
    },

    deleteProperty(t, prop) {
      let effectiveProp = prop;
      if (t === rawState && prop === 'hrZoneThresholds') {
        effectiveProp = 'hrThresholds';
      }
      if (effectiveProp in t) {
        const currentRoot = rootKeyMap.get(t) || (t === rawState ? effectiveProp : null);
        const success = Reflect.deleteProperty(t, effectiveProp);
        if (success) {
          scheduleBatch(currentRoot || effectiveProp);
        }
        return success;
      }
      return true;
    }
  };

  const proxy = new Proxy(target, handler);
  proxyCache.set(target, proxy);
  reverseProxyCache.set(proxy, target);
  return proxy;
}

// Instantiate internal mutable state tree and root proxy
const rawState = getInitialState();
export const store = createDeepProxy(rawState, null);

if (typeof window !== 'undefined') {
  window.state = store;
  window.cachedWorkouts = store.workouts;
}

// --- Subscription APIs ---

/**
 * Subscribes to changes on a specific top-level store key.
 * @param {string} key 
 * @param {Function} callback 
 * @returns {Function} Unsubscribe function
 */
export function subscribeKey(key, callback) {
  if (typeof callback !== 'function' || !key) return () => {};
  if (!subscribersByKey.has(key)) {
    subscribersByKey.set(key, new Set());
  }
  subscribersByKey.get(key).add(callback);
  return () => {
    const set = subscribersByKey.get(key);
    if (set) {
      set.delete(callback);
      if (set.size === 0) subscribersByKey.delete(key);
    }
  };
}

/**
 * Subscribes to all store changes.
 * @param {Function} callback Receives (mutatedKeys, store)
 * @returns {Function} Unsubscribe function
 */
export function subscribeToStore(callback) {
  if (typeof callback !== 'function') return () => {};
  globalSubscribers.add(callback);
  return () => {
    globalSubscribers.delete(callback);
  };
}

/**
 * Unified subscription helper.
 * subscribe('workouts', fn) -> subscribeKey('workouts', fn)
 * subscribe(fn) -> subscribeToStore(fn)
 */
export function subscribe(keyOrCallback, callback) {
  if (typeof keyOrCallback === 'function') {
    return subscribeToStore(keyOrCallback);
  }
  return subscribeKey(keyOrCallback, callback);
}

// --- Storage Persistence & Hydration ---

export function getStorageKey() {
  if (store.currentUser && store.currentUser.id && store.currentUser.id !== 'guest-preview-user') {
    return `pulsefit_state_v2_${store.currentUser.id}`;
  }
  return 'pulsefit_state_v2';
}

/**
 * Hydrates persisted user preferences from localStorage into store.
 * @returns {boolean} True if preferences were hydrated
 */
export function loadStateFromStorage() {
  if (typeof localStorage === 'undefined') return false;
  try {
    const key = getStorageKey();
    const raw = localStorage.getItem(key);
    let data = null;
    if (raw) {
      data = JSON.parse(raw);
    } else {
      // Legacy backwards-compatibility fallback
      const legacyRaw = localStorage.getItem('pulsefit_state_v2') || localStorage.getItem('pulsefit_state') || localStorage.getItem('roxkit_state');
      if (legacyRaw) {
        data = JSON.parse(legacyRaw);
        if (key !== 'pulsefit_state_v2') {
          // Migrate and cleanup
          localStorage.setItem(key, legacyRaw);
          localStorage.removeItem('pulsefit_state_v2');
          localStorage.removeItem('pulsefit_state');
          localStorage.removeItem('roxkit_state');
        }
      }
    }

    if (data && typeof data === 'object') {
      if (data.goals && typeof data.goals === 'object' && !Array.isArray(data.goals)) {
        const stations = (data.goals.stations && typeof data.goals.stations === 'object' && !Array.isArray(data.goals.stations)) ? data.goals.stations : {};
        store.goals = {
          ...store.goals,
          ...data.goals,
          stations: {
            ...(store.goals.stations || {}),
            ...stations
          }
        };
      }
      if (data.hrThresholds && typeof data.hrThresholds === 'object' && !Array.isArray(data.hrThresholds)) {
        store.hrThresholds = { ...store.hrThresholds, ...data.hrThresholds };
      } else if (data.hrZoneThresholds && typeof data.hrZoneThresholds === 'object' && !Array.isArray(data.hrZoneThresholds)) {
        store.hrThresholds = { ...store.hrThresholds, ...data.hrZoneThresholds };
      }
      if (data.stationAlternatives && typeof data.stationAlternatives === 'object' && !Array.isArray(data.stationAlternatives)) {
        store.stationAlternatives = data.stationAlternatives;
      }
      if (data.weeklyAdjustments && typeof data.weeklyAdjustments === 'object' && !Array.isArray(data.weeklyAdjustments)) {
        store.weeklyAdjustments = { ...store.weeklyAdjustments, ...data.weeklyAdjustments };
      }
      if (data.weeklyZoneAdjustments && typeof data.weeklyZoneAdjustments === 'object' && !Array.isArray(data.weeklyZoneAdjustments)) {
        store.weeklyZoneAdjustments = { ...store.weeklyZoneAdjustments, ...data.weeklyZoneAdjustments };
      }
      if (data.weeklyZoneGoals && typeof data.weeklyZoneGoals === 'object' && !Array.isArray(data.weeklyZoneGoals)) {
        store.weeklyZoneGoals = { ...store.weeklyZoneGoals, ...data.weeklyZoneGoals };
      }
      if (data.uiPreferences && typeof data.uiPreferences === 'object' && !Array.isArray(data.uiPreferences)) {
        store.uiPreferences = { ...store.uiPreferences, ...data.uiPreferences };
      }
      if (data.strava && typeof data.strava === 'object' && !Array.isArray(data.strava)) {
        store.strava = { ...store.strava, ...data.strava };
      }
      if (typeof data.geminiApiKey === 'string') store.geminiApiKey = data.geminiApiKey;
      if (typeof data.geminiInstructions === 'string') store.geminiInstructions = data.geminiInstructions;
      if (Array.isArray(data.plannedWorkouts)) store.plannedWorkouts = data.plannedWorkouts;
      if (typeof data.biometricLockEnabled === 'boolean') store.biometricLockEnabled = data.biometricLockEnabled;
      if (data.todayHealth && typeof data.todayHealth === 'object') store.todayHealth = data.todayHealth;
      if (data.dailyRecovery && typeof data.dailyRecovery === 'object') store.dailyRecovery = data.dailyRecovery;
      return true;
    }
  } catch (err) {
    console.warn('[Store] Failed to load preferences from storage:', err);
  }
  return false;
}

/**
 * Persists user preferences to localStorage ('pulsefit_state_v2').
 * Sensitive session tokens, user records, and workouts are never persisted here.
 */
export function saveStateToStorage() {
  if (typeof localStorage === 'undefined') return;
  try {
    const payload = {
      goals: rawState.goals,
      hrThresholds: rawState.hrThresholds,
      stationAlternatives: rawState.stationAlternatives,
      weeklyAdjustments: rawState.weeklyAdjustments,
      weeklyZoneAdjustments: rawState.weeklyZoneAdjustments,
      weeklyZoneGoals: rawState.weeklyZoneGoals,
      uiPreferences: rawState.uiPreferences,
      strava: rawState.strava,
      geminiApiKey: rawState.geminiApiKey || '',
      geminiInstructions: rawState.geminiInstructions || '',
      plannedWorkouts: Array.isArray(rawState.plannedWorkouts) ? rawState.plannedWorkouts : [],
      biometricLockEnabled: !!rawState.biometricLockEnabled,
      todayHealth: rawState.todayHealth || null,
      dailyRecovery: rawState.dailyRecovery || null,
      _persistedAt: new Date().toISOString()
    };
    const key = getStorageKey();
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (err) {
    console.warn('[Store] Failed to persist state to storage:', err);
  }
}

// --- Domain Action Setters ---

/**
 * Updates the workout array in store and syncs window.cachedWorkouts.
 * @param {Array} workouts 
 * @returns {Array} Updated workouts
 */
export function setWorkouts(workouts) {
  const list = Array.isArray(workouts) ? workouts : [];
  store.workouts = list;
  if (typeof window !== 'undefined') {
    window.cachedWorkouts = store.workouts;
  }
  return store.workouts;
}

/**
 * Updates a single workout in store by ID.
 * @param {string|number} workoutId 
 * @param {Object} patch 
 * @returns {Object|null} The updated workout or null
 */
export function updateWorkout(workoutId, patch) {
  if (workoutId === undefined || workoutId === null || workoutId === '' || !patch || typeof patch !== 'object') return null;
  const list = store.workouts;
  const workout = list.find(w => w && (w.id === workoutId || w._id === workoutId || String(w.id) === String(workoutId)));
  if (!workout) return null;
  Object.assign(workout, patch);
  return workout;
}

/**
 * Deletes a workout from store by ID.
 * @param {string|number} workoutId 
 * @returns {boolean} True if removed, false if not found
 */
export function deleteWorkout(workoutId) {
  if (workoutId === undefined || workoutId === null || workoutId === '') return false;
  const list = store.workouts;
  const idx = list.findIndex(w => w && (w.id === workoutId || w._id === workoutId || String(w.id) === String(workoutId)));
  if (idx === -1) return false;
  list.splice(idx, 1);
  return true;
}

/**
 * Updates weekly goals in store.
 * @param {Object} goals 
 * @returns {Object} Updated goals
 */
export function setGoals(goals) {
  if (!goals || typeof goals !== 'object' || Array.isArray(goals)) return store.goals;
  const currentGoals = store.goals || {};
  const currentStations = currentGoals.stations || {};
  const newStations = (goals.stations && typeof goals.stations === 'object' && !Array.isArray(goals.stations)) ? { ...currentStations, ...goals.stations } : currentStations;

  store.goals = {
    ...currentGoals,
    ...goals,
    stations: newStations
  };
  return store.goals;
}

/**
 * Updates HR zone threshold boundaries in store.
 * @param {Object} thresholds 
 * @returns {Object} Updated hrThresholds
 */
export function setHrThresholds(thresholds) {
  if (!thresholds || typeof thresholds !== 'object') return store.hrThresholds;
  store.hrThresholds = {
    ...store.hrThresholds,
    ...thresholds
  };
  return store.hrThresholds;
}
export const setHrZoneThresholds = setHrThresholds;

/**
 * Sets current authenticated user.
 * @param {Object|null} user 
 * @returns {Object|null}
 */
export function setCurrentUser(user) {
  store.currentUser = user || null;
  return store.currentUser;
}

/**
 * Sets current active view.
 * @param {string} viewId 
 * @returns {string}
 */
export function setActiveView(viewId) {
  store.activeView = viewId;
  return store.activeView;
}

/**
 * Sets or updates daily recovery metrics in store.
 * @param {Object} recovery 
 * @returns {Object|null}
 */
export function setDailyRecovery(recovery) {
  if (!recovery || typeof recovery !== 'object') return store.dailyRecovery;
  store.dailyRecovery = { ...(store.dailyRecovery || {}), ...recovery };
  return store.dailyRecovery;
}

/**
 * Sets today's Apple Health telemetry metrics in store.
 * @param {Object|null} health 
 * @returns {Object|null}
 */
export function setTodayHealth(health) {
  store.todayHealth = health || null;
  return store.todayHealth;
}

/**
 * Resets store state in place back to defaults, optionally overlaying customState.
 * Maintains proxy identity for zero-churn backwards compatibility.
 * @param {Object} [customState=null] 
 * @returns {Object} The store proxy
 */
export function resetStore(customState = null) {
  const defaults = getInitialState();
  const next = customState && typeof customState === 'object' ? { ...defaults, ...customState } : defaults;

  // Clear existing keys from rawState that aren't in next
  for (const key of Object.keys(rawState)) {
    if (!(key in next)) {
      delete store[key];
    }
  }

  // Assign next keys to store (triggers reactivity)
  for (const [key, val] of Object.entries(next)) {
    store[key] = val;
  }

  if (typeof window !== 'undefined') {
    window.cachedWorkouts = store.workouts;
  }

  return store;
}

export default store;
